import {
  CLAUDIA_ATTACHMENT_IMAGE_MIME_TYPES,
  formatArtifactMaxFileSize,
  getArtifactMaxFileSizeBytes,
  MODEL_IMAGE_MAX_EDGE,
  MODEL_IMAGE_VARIANT_MAX_BYTES,
} from "@repo/shared";

export type ImageDimensions = {
  width: number;
  height: number;
};

export interface PreparedImageAsset {
  original: File;
  originalWidth?: number;
  originalHeight?: number;
  modelVariant?: {
    file: File;
    width: number;
    height: number;
  };
}

type ImageVariantStage = {
  mimeType: "image/png" | "image/jpeg";
  maxEdge: number;
  quality?: number;
  fillWhite: boolean;
};

export function isSupportedClaudiaImage(file: File): boolean {
  return CLAUDIA_ATTACHMENT_IMAGE_MIME_TYPES.includes(
    file.type as (typeof CLAUDIA_ATTACHMENT_IMAGE_MIME_TYPES)[number]
  );
}

export function isHeicImage(file: File): boolean {
  const name = file.name.toLowerCase();
  return (
    file.type === "image/heic" ||
    file.type === "image/heif" ||
    name.endsWith(".heic") ||
    name.endsWith(".heif")
  );
}

export async function isAnimatedGif(file: File): Promise<boolean> {
  if (file.type !== "image/gif") return false;
  const buffer = await file.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  let frames = 0;

  for (let index = 0; index <= bytes.length - 3; index += 1) {
    if (bytes[index] === 0x21 && bytes[index + 1] === 0xf9 && bytes[index + 2] === 0x04) {
      frames += 1;
      if (frames > 1) return true;
    }
  }

  return false;
}

export async function readImageDimensions(file: File): Promise<ImageDimensions> {
  const url = URL.createObjectURL(file);
  try {
    const image = new Image();
    image.decoding = "async";
    const loaded = new Promise<void>((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () => reject(new Error(`Could not read ${file.name}`));
    });
    image.src = url;
    await loaded;
    return {
      width: image.naturalWidth,
      height: image.naturalHeight,
    };
  } finally {
    URL.revokeObjectURL(url);
  }
}

function dimensionsForMaxEdge(dimensions: ImageDimensions, maxEdge: number): ImageDimensions {
  const edge = Math.max(dimensions.width, dimensions.height);
  const scale = Math.min(1, maxEdge / edge);
  return {
    width: Math.max(1, Math.round(dimensions.width * scale)),
    height: Math.max(1, Math.round(dimensions.height * scale)),
  };
}

async function blobFromCanvas(
  canvas: HTMLCanvasElement,
  mimeType: ImageVariantStage["mimeType"],
  quality?: number
): Promise<Blob | null> {
  return new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, mimeType, quality));
}

async function buildImageVariant(
  file: File,
  dimensions: ImageDimensions,
  stage: ImageVariantStage
): Promise<{ file: File; width: number; height: number } | null> {
  const outputDimensions = dimensionsForMaxEdge(dimensions, stage.maxEdge);
  const bitmap = await createImageBitmap(file);
  try {
    const canvas = document.createElement("canvas");
    canvas.width = outputDimensions.width;
    canvas.height = outputDimensions.height;
    const context = canvas.getContext("2d");
    if (!context) return null;
    if (stage.fillWhite) {
      context.fillStyle = "#fff";
      context.fillRect(0, 0, outputDimensions.width, outputDimensions.height);
    }
    context.drawImage(bitmap, 0, 0, outputDimensions.width, outputDimensions.height);
    const blob = await blobFromCanvas(canvas, stage.mimeType, stage.quality);
    if (!blob) return null;
    return {
      file: new File([blob], file.name, {
        type: stage.mimeType,
        lastModified: file.lastModified,
      }),
      ...outputDimensions,
    };
  } finally {
    bitmap.close();
  }
}

async function createModelImageVariant(
  file: File,
  dimensions: ImageDimensions
): Promise<PreparedImageAsset["modelVariant"]> {
  const stages: ImageVariantStage[] = [
    ...(file.type === "image/png"
      ? [
          {
            mimeType: "image/png" as const,
            maxEdge: MODEL_IMAGE_MAX_EDGE,
            fillWhite: false,
          },
        ]
      : []),
    {
      mimeType: "image/jpeg",
      maxEdge: MODEL_IMAGE_MAX_EDGE,
      quality: 0.85,
      fillWhite: true,
    },
    {
      mimeType: "image/jpeg",
      maxEdge: MODEL_IMAGE_MAX_EDGE,
      quality: 0.7,
      fillWhite: true,
    },
    {
      mimeType: "image/jpeg",
      maxEdge: 1280,
      quality: 0.7,
      fillWhite: true,
    },
    {
      mimeType: "image/jpeg",
      maxEdge: 1024,
      quality: 0.6,
      fillWhite: true,
    },
  ];

  let smallestVariant: PreparedImageAsset["modelVariant"];
  for (const stage of stages) {
    const variant = await buildImageVariant(file, dimensions, stage);
    if (!variant) continue;
    if (!smallestVariant || variant.file.size < smallestVariant.file.size) {
      smallestVariant = variant;
    }
    if (variant.file.size <= MODEL_IMAGE_VARIANT_MAX_BYTES) {
      return variant;
    }
  }

  if (smallestVariant) {
    return smallestVariant;
  }
  throw new Error(`Could not prepare ${file.name} for model input.`);
}

export async function prepareImageAsset(file: File): Promise<PreparedImageAsset> {
  if (isHeicImage(file)) {
    throw new Error("HEIC images are not supported yet. Use PNG, JPEG, WebP, or a static GIF.");
  }
  if (!isSupportedClaudiaImage(file)) {
    throw new Error("Only PNG, JPEG, WebP, and static GIF images can be attached.");
  }
  if (file.size > getArtifactMaxFileSizeBytes(file.name, file.type)) {
    throw new Error(
      `Images must be smaller than ${formatArtifactMaxFileSize(file.name, file.type)}.`
    );
  }
  if (await isAnimatedGif(file)) {
    throw new Error("Animated GIFs are not supported yet. Use a static image instead.");
  }

  const dimensions = await readImageDimensions(file).catch(() => undefined);
  if (!dimensions) {
    return { original: file };
  }

  const maxEdge = Math.max(dimensions.width, dimensions.height);
  const needsVariant = maxEdge > MODEL_IMAGE_MAX_EDGE || file.size > MODEL_IMAGE_VARIANT_MAX_BYTES;
  if (!needsVariant) {
    return { original: file, originalWidth: dimensions.width, originalHeight: dimensions.height };
  }

  return {
    original: file,
    originalWidth: dimensions.width,
    originalHeight: dimensions.height,
    modelVariant: await createModelImageVariant(file, dimensions),
  };
}
